import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttitudeBehaviorCatalogItem, FundamentalCatalogItem } from '@/api/types';
import { FundamentalIcon } from '@/components/common/FundamentalIcon';
import { getAttitudeDescription, getAttitudeName } from '@/hooks/useFundamentals';
import { activeAttitudes, activeHabitsForAttitude, allActiveHabits, orphanHabits } from './habitSelection';

const DEFAULT_FUNDAMENTAL_COLOR = '#342F82';
const OPEN_HABITS_KEY = 'fundamentals.openHabits';

/** Qué grupo de hábitos pidió abrir el usuario. */
export interface HabitSelection {
  fundamental: FundamentalCatalogItem;
  /** Nombre del comportamiento; ausente cuando se abrió desde el fundamental completo. */
  attitudeName?: string;
  habitScope: { kind: 'attitude'; attitudeId: string } | { kind: 'orphans' } | { kind: 'all' };
}

export function fundamentalDisplayName(fundamental: FundamentalCatalogItem, lang: string): string {
  return fundamental.shortNames?.[lang]
    ?? fundamental.shortName
    ?? fundamental.names?.[lang]
    ?? fundamental.names?.['es']
    ?? fundamental.names?.['en']
    ?? fundamental.id;
}

export function fundamentalDisplayDescription(
  fundamental: FundamentalCatalogItem,
  lang: string,
): string {
  return fundamental.descriptions?.[lang]
    ?? fundamental.descriptions?.['es']
    ?? fundamental.descriptions?.['en']
    ?? fundamental.description
    ?? '';
}

export function fundamentalColor(fundamental: FundamentalCatalogItem): string {
  return fundamental.color ?? DEFAULT_FUNDAMENTAL_COLOR;
}

interface AttitudeRowProps {
  attitude: AttitudeBehaviorCatalogItem;
  lang: string;
  hasHabits: boolean;
  onOpen: () => void;
}

function AttitudeRow({ attitude, lang, hasHabits, onOpen }: AttitudeRowProps) {
  const { t } = useTranslation();
  const attitudeName = getAttitudeName(attitude, lang);
  const attitudeDescription = getAttitudeDescription(attitude, lang);
  const body = (
    <>
      <span className="fund-cat-att-icon">
        <FundamentalIcon
          icon={attitude.image}
          imageClassName="fund-icon-img fund-icon-img--sm"
          fallbackIcon="fa-comment-dots"
        />
      </span>
      <span className="fund-cat-att-text">
        <span className="fund-cat-att-name">{attitudeName}</span>
        {attitudeDescription && <span className="fund-cat-att-desc">{attitudeDescription}</span>}
      </span>
    </>
  );

  if (!hasHabits) {
    return <div className="fund-cat-att-item">{body}</div>;
  }

  return (
    <button
      type="button"
      className="fund-cat-att-item fund-cat-clickable"
      onClick={onOpen}
      aria-label={t(OPEN_HABITS_KEY, { name: attitudeName })}
    >
      {body}
      <i className="fas fa-chevron-right fund-cat-chevron" aria-hidden="true" />
    </button>
  );
}

interface OrphanRowProps {
  label: string;
  onOpen: () => void;
}

function OrphanRow({ label, onOpen }: OrphanRowProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className="fund-cat-att-item fund-cat-clickable"
      onClick={onOpen}
      aria-label={t(OPEN_HABITS_KEY, { name: label })}
    >
      <span className="fund-cat-att-icon">
        <i className="fas fa-list-check" aria-hidden="true" />
      </span>
      <span className="fund-cat-att-text">
        <span className="fund-cat-att-name">{label}</span>
      </span>
      <i className="fas fa-chevron-right fund-cat-chevron" aria-hidden="true" />
    </button>
  );
}

const OPEN_MICROSITE_KEY = 'fundamentals.openMicrosite';

/**
 * Cabecera (icono + título) de un fundamental. Cuando el fundamental tiene `micrositeUrl`,
 * el icono+título se vuelven un enlace al micrositio (abre en pestaña nueva), separado del
 * clic que abre el modal de hábitos. Sin micrositio, es texto plano (fragmento).
 */
function FundamentalHead({
  fundamental,
  name,
  micrositeUrl,
  ariaLabel,
}: {
  fundamental: FundamentalCatalogItem;
  name: string;
  micrositeUrl?: string;
  ariaLabel: string;
}) {
  const icon = (
    <span className="fund-cat-fund-icon">
      {/* BUG (Julio, 2026-08-17): `icon` (ícono elegido en el picker) e `image` (imagen propia subida
          por el admin) son campos separados del catálogo — solo se leía `icon`, así que una imagen
          subida sin ningún ícono de picker seleccionado nunca se mostraba aquí. `icon` manda cuando
          hay uno curado; `image` es el resguardo, igual que ya resuelven Actitudes/Daily Habits. */}
      <FundamentalIcon icon={fundamental.icon || fundamental.image} />
    </span>
  );

  if (!micrositeUrl) {
    return (
      <>
        {icon}
        <span className="fund-cat-name" data-testid="fund-cat-name">{name}</span>
      </>
    );
  }

  return (
    <a
      className="fund-cat-microsite-head fund-cat-clickable"
      href={micrositeUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={ariaLabel}
    >
      {icon}
      <span className="fund-cat-name" data-testid="fund-cat-name">
        {name}
        <i className="fas fa-arrow-up-right-from-square fund-cat-microsite-ext" aria-hidden="true" />
      </span>
    </a>
  );
}

export interface FundamentalCardProps {
  fundamental: FundamentalCatalogItem;
  lang: string;
  /** Etiqueta traducida para la fila de hábitos sin comportamiento. */
  orphanLabel: string;
  onSelect: (selection: HabitSelection) => void;
}

export function FundamentalCard({ fundamental, lang, orphanLabel, onSelect }: FundamentalCardProps) {
  const { t } = useTranslation();
  const color = fundamentalColor(fundamental);
  const name = fundamentalDisplayName(fundamental, lang);
  const attitudes = activeAttitudes(fundamental);
  const micrositeUrl = fundamental.micrositeUrl?.trim() || undefined;
  const openAll = () => onSelect({ fundamental, habitScope: { kind: 'all' } });
  // BUG (Julio, 2026-08-19) — se hoistea (antes solo se calculaba dentro de la rama compacta):
  // la variante expandida también necesita la descripción, debajo de `.fund-cat-header`.
  const description = fundamentalDisplayDescription(fundamental, lang);

  if (attitudes.length === 0) {
    const hasHabits = allActiveHabits(fundamental).length > 0;

    // Sin micrositio se conserva el comportamiento original: toda la tarjeta abre el modal
    // (para no perder el clic en el título/icono cuando no hay micrositio que separar).
    if (!micrositeUrl) {
      const cardBody = (
        <>
          <span className="fund-cat-fund-icon">
            {/* BUG (Julio, 2026-08-17): `icon` (ícono elegido en el picker) e `image` (imagen propia subida
          por el admin) son campos separados del catálogo — solo se leía `icon`, así que una imagen
          subida sin ningún ícono de picker seleccionado nunca se mostraba aquí. `icon` manda cuando
          hay uno curado; `image` es el resguardo, igual que ya resuelven Actitudes/Daily Habits. */}
      <FundamentalIcon icon={fundamental.icon || fundamental.image} />
          </span>
          <span className="fund-cat-name" data-testid="fund-cat-name">{name}</span>
          {description && <span className="fund-cat-fund-desc">{description}</span>}
        </>
      );

      if (!hasHabits) {
        return (
          <div className="fund-cat-card fund-cat-card--compact" style={{ '--fund-color': color } as CSSProperties}>
            {cardBody}
          </div>
        );
      }

      return (
        <button
          type="button"
          className="fund-cat-card fund-cat-card--compact fund-cat-clickable"
          style={{ '--fund-color': color } as CSSProperties}
          onClick={openAll}
          aria-label={t(OPEN_HABITS_KEY, { name })}
        >
          {cardBody}
        </button>
      );
    }

    // Con micrositio: el icono+título enlazan al micrositio; el resto (descripción) abre el modal.
    return (
      <div className="fund-cat-card fund-cat-card--compact" style={{ '--fund-color': color } as CSSProperties}>
        <FundamentalHead
          fundamental={fundamental}
          name={name}
          micrositeUrl={micrositeUrl}
          ariaLabel={t(OPEN_MICROSITE_KEY, { name })}
        />
        {hasHabits ? (
          <button
            type="button"
            className="fund-cat-fund-desc fund-cat-desc-btn fund-cat-clickable"
            onClick={openAll}
            aria-label={t(OPEN_HABITS_KEY, { name })}
          >
            {description || t(OPEN_HABITS_KEY, { name })}
          </button>
        ) : (
          description && <span className="fund-cat-fund-desc">{description}</span>
        )}
      </div>
    );
  }

  const orphans = orphanHabits(fundamental);

  return (
    <div
      className="fund-cat-card fund-cat-card--expanded"
      style={{ '--fund-color': color } as CSSProperties}
    >
      <div className="fund-cat-header">
        <FundamentalHead
          fundamental={fundamental}
          name={name}
          micrositeUrl={micrositeUrl}
          ariaLabel={t(OPEN_MICROSITE_KEY, { name })}
        />
      </div>
      {/* BUG (Julio, 2026-08-19) — faltaba la descripción del Fundamental entre el header y la
          lista de actitudes; sin ella, la lista arrancaba pegada justo debajo del header. Mismo
          elemento/clase que ya usan las variantes compactas (span, no p — sin margin propio que
          se sume al `gap` del flex-column de `.fund-cat-card`). */}
      {description && <span className="fund-cat-fund-desc">{description}</span>}
      <div className="fund-cat-att-list">
        {attitudes.map(attitude => (
          <AttitudeRow
            key={attitude.id}
            attitude={attitude}
            lang={lang}
            hasHabits={activeHabitsForAttitude(fundamental, attitude.id).length > 0}
            onOpen={() => onSelect({
              fundamental,
              attitudeName: getAttitudeName(attitude, lang),
              habitScope: { kind: 'attitude', attitudeId: attitude.id },
            })}
          />
        ))}
        {orphans.length > 0 && (
          <OrphanRow
            label={orphanLabel}
            onOpen={() => onSelect({
              fundamental,
              attitudeName: orphanLabel,
              habitScope: { kind: 'orphans' },
            })}
          />
        )}
      </div>
    </div>
  );
}
